import { chatbotApi } from '@/helpers/api'
import { useAsyncState } from '@vueuse/core'
import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'

// interface IQnA {
//   question: IChatMessage
//   answer?: IChatMessage
//   rating?: AnswerRating
// }

// export enum WindowState {
//   Normal,
//   Maximized,
// }

export enum AnswerRating {
  Up,
  Down,
}

export enum ChatType {
  Question,
  Answer,
  Error,
}

export interface IChatMessage {
  type: ChatType
  message: string
  time: Date
  rating?: AnswerRating
}

///  test code
// import MockAdapter from 'axios-mock-adapter'
// const mock = new MockAdapter(chatbotApi, { delayResponse: 2000 })
// mock
//   .onAny('/status')
//   // .reply(200, {
//   //   available: true,
//   //   max_chats: 10,
//   // })
//   .reply(500, {})
///

export const useChatbotStore = defineStore('Chatbot', () => {
  const firstVisit = ref(false)
  const patchNotesVersion = ref(0)
  const chatId = ref('') // ref(uuid())
  const chats = ref<IChatMessage[]>([])
  // const windowState = ref(WindowState.Normal)
  const isWindowMinimized = ref(true) // default minimized
  const isWindowMaximized = ref(false)
  const streamMessage = ref('') // chatbot answer stream message
  const questionInputbox = ref('')
  const systemError = ref(false)
  const maxQuestionsPerSession = 10

  const {
    state,
    isReady,
    error: apiError,
    execute: checkStatus,
  } = useAsyncState(
    () => chatbotApi.get('/status').then(({ data }) => data),
    { available: false, max_chats: 0 },
    // { available: true, max_chats: 10 }, // test code
    { immediate: false }
  )

  const apiAvailability = computed(() => state.value.available)

  // chatapi request messages
  const chatApiRequestMessages = computed(() => {
    const arr = []
    const length = chats.value.length
    for (let i = 0; i < length; i++) {
      const chat = chats.value[i]
      // skip the question if it's answer is not normal
      if (chat.type === ChatType.Question && i < length - 1 && chats.value[i + 1].type !== ChatType.Answer) continue
      if (chat.type === ChatType.Question || chat.type === ChatType.Answer) {
        arr.push({
          role: chat.type === ChatType.Question ? 'user' : 'assistant',
          content: chat.message,
        })
      }
    }
    return arr
  })

  const questionsCount = computed(() => Math.floor(chatApiRequestMessages.value.length / 2))

  async function sendQuestion() {
    // if (sessionEnd.value === true) return

    const data = {
      chat_id: chatId.value,
      messages: chatApiRequestMessages.value,
    }

    streamMessage.value = ''
    systemError.value = false
    try {
      // const aborter = new AbortController();
      const response = await fetch('/api/maplestory/no-auth/patchnotes-bot/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        // signal: aborter.signal
      })
      const reader = response.body?.getReader()
      if (!reader) throw new Error('no body was returned')
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          if (response.ok) addAnswer(streamMessage.value)
          else addError(response.status, streamMessage.value)
          break
        }
        const text = new TextDecoder().decode(value)
        streamMessage.value += text
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return
      }
      console.error(error)
      addError(500, error.toString())
      // streamMessage.value = error.toString()
    }
  }

  // prevent duplication
  const isReadyForAsking = computed(
    () =>
      questionInputbox.value.trim() !== '' &&
      (chats.value.length === 0 || chats.value[chats.value.length - 1].type !== ChatType.Question)
  )

  const addQuestion = () => {
    if (!isReadyForAsking.value) return

    chats.value.push({
      type: ChatType.Question,
      message: questionInputbox.value,
      time: new Date(),
    })
    questionInputbox.value = ''
    // ask chatbot
    sendQuestion()
  }

  const addError = (code: number, message: string) => {
    try {
      const errorCode = JSON.parse(message).code
      message = ''
      switch (errorCode) {
        case 'HARMFUL_CONTENT':
          message = errorCode
          break
        case 'BAD_REQUEST':
          refreshSession() // reset for recovering the bad request abnormal situation
          return
        // default:
        //   message = `Unknown error occurred - ${errorCode}`
      }
    } catch {
      message = ''
    }
    if (message) {
      chats.value.push({
        type: ChatType.Error,
        message,
        time: new Date(),
      })
    } else {
      const question = chats.value.pop()
      if (question?.message) questionInputbox.value = question?.message
      systemError.value = true
    }
    saveData()
  }

  const addAnswer = (message: string) => {
    if (chats.value.length > 0 && chats.value[chats.value.length - 1].type === ChatType.Answer) return // prevent duplication
    chats.value.push({
      type: ChatType.Answer,
      message,
      time: new Date(),
    })

    // remove first 1 pair conversation
    while (chatApiRequestMessages.value.length > maxQuestionsPerSession * 2) {
      console.log('removing chat history one', chatApiRequestMessages.value.length, chats.value.length)
      chats.value.shift()
    }
    // saving point
    saveData()
  }

  const saveData = () => {
    const data = {
      chatId: chatId.value,
      chats: chats.value,
      isWindowMinimized: isWindowMinimized.value,
      isWindowMaximized: isWindowMaximized.value,
      questionInputbox: questionInputbox.value,
      patchNoteVersion: patchNotesVersion.value,
      version: '1.0.0',
    }
    localStorage.setItem('chatbot.patchNotes', JSON.stringify(data))
  }

  const loadData = () => {
    const str = localStorage.getItem('chatbot.patchNotes')
    if (!str) return false
    try {
      const data = JSON.parse(str)
      if (data.version === undefined || data.version !== '1.0.0') return false
      if (data.patchNoteVersion === patchNotesVersion.value) {
        if (data.chatId !== undefined) chatId.value = data.chatId
        if (data.chats !== undefined) chats.value = data.chats
        if (data.questionInputbox !== undefined) questionInputbox.value = data.questionInputbox
      }
      if (data.isWindowMaximized !== undefined) isWindowMaximized.value = data.isWindowMaximized
      if (data.isWindowMinimized !== undefined) isWindowMinimized.value = data.isWindowMinimized
      return true
    } catch {
      return false
    }
  }

  watch([isWindowMaximized, isWindowMinimized], () => {
    saveData()
  })

  const refreshSession = () => {
    chatId.value = uuid()
    chats.value = []
    saveData()
  }

  watch(
    apiAvailability,
    (val) => {
      if (!val) return
      if (!chatId.value) chatId.value = uuid()
      // if last chat was a question, remove it and put it in the input box
      if (chats.value.length % 2 === 1) {
        const chat = chats.value.pop()
        if (chat) questionInputbox.value = chat.message
      }
      setTimeout(() => {
        saveData() // for saving the not-first-visit status
      }, 1000)
    }
    // { immediate: true }
  )

  // init
  const init = async (version: number) => {
    await checkStatus()
    patchNotesVersion.value = version
    if (loadData() === false) firstVisit.value = true
  }

  return {
    init,
    // checkStatus,
    isReady,
    apiError,
    apiAvailability,
    firstVisit,
    patchNotesVersion,
    chatId,
    chats,
    systemError,
    addQuestion,
    isReadyForAsking,
    isWindowMinimized,
    isWindowMaximized,
    streamMessage,
    questionInputbox,
    refreshSession,
    maxQuestionsPerSession,
    questionsCount,
    chatApiRequestMessages,
  }
})
